/**
 * Position Manager Page - Phase 1
 * ATR-based position management with stop-loss/take-profit analysis
 * 
 * Features:
 * - ATR Calculator: lookup ATR for any symbol
 * - Position Analyzer: entry price + ATR = stop/target levels
 * - Position Sizer: risk-based share quantity calculator
 * - Active Positions: live Alpaca positions with ATR overlay
 * - Take-Profit Planner: staged exit plan generator
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { alpacaAPI } from '../services/api';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';
import {
    TrendingUp, TrendingDown, Target, Shield, DollarSign,
    Calculator, BarChart3, RefreshCw, ArrowUpCircle, ArrowDownCircle,
    AlertTriangle, CheckCircle, Crosshair, Layers, Activity,
    ChevronDown, ChevronUp, Info
} from 'lucide-react';

// Use the same api base that the rest of the app uses
const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
});

const PositionManager = () => {
    const { hasAlpacaKeys } = useAuth();

    // ATR Calculator state
    const [atrSymbol, setAtrSymbol] = useState('');
    const [atrPeriod, setAtrPeriod] = useState(14);
    const [atrResult, setAtrResult] = useState(null);
    const [atrLoading, setAtrLoading] = useState(false);

    // Position Analyzer state
    const [analyzeSymbol, setAnalyzeSymbol] = useState('');
    const [analyzeEntry, setAnalyzeEntry] = useState('');
    const [analyzeSide, setAnalyzeSide] = useState('long');
    const [analyzeResult, setAnalyzeResult] = useState(null);
    const [analyzeLoading, setAnalyzeLoading] = useState(false);

    // Position Sizer state
    const [sizeAccountValue, setSizeAccountValue] = useState('');
    const [sizeRiskPercent, setSizeRiskPercent] = useState('1');
    const [sizeEntryPrice, setSizeEntryPrice] = useState('');
    const [sizeStopLoss, setSizeStopLoss] = useState('');
    const [sizeResult, setSizeResult] = useState(null);

    // Active Positions state
    const [activePositions, setActivePositions] = useState([]);
    const [positionsLoading, setPositionsLoading] = useState(false);
    const [expandedPosition, setExpandedPosition] = useState(null);

    // Active tab
    const [activeTab, setActiveTab] = useState('calculator');

    // Auto-refresh
    const [refreshing, setRefreshing] = useState(false);

    // ============ ATR CALCULATOR ============
    const handleATRLookup = async () => {
        if (!atrSymbol.trim()) {
            toast.error('Enter a symbol');
            return;
        }
        setAtrLoading(true);
        try {
            const res = await fetch(`/api/positions/atr/${atrSymbol.toUpperCase()}?period=${atrPeriod}`, {
                headers: getAuthHeaders(),
            });
            const data = await res.json();
            if (data.success) {
                setAtrResult(data.data);
                toast.success(`ATR loaded for ${atrSymbol.toUpperCase()}`);
            } else {
                toast.error(data.error || 'Failed to fetch ATR');
            }
        } catch (err) {
            toast.error('Failed to connect to server');
        } finally {
            setAtrLoading(false);
        }
    };

    // ============ POSITION ANALYZER ============
    const handleAnalyze = async () => {
        if (!analyzeSymbol.trim()) {
            toast.error('Enter a symbol');
            return;
        }
        setAnalyzeLoading(true);
        try {
            const res = await fetch('/api/positions/analyze', {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    symbol: analyzeSymbol.toUpperCase(),
                    entryPrice: analyzeEntry ? parseFloat(analyzeEntry) : null,
                    side: analyzeSide,
                    period: atrPeriod,
                }),
            });
            const data = await res.json();
            if (data.success) {
                setAnalyzeResult(data.data);
                toast.success('Analysis complete');
            } else {
                toast.error(data.error || 'Analysis failed');
            }
        } catch (err) {
            toast.error('Failed to connect to server');
        } finally {
            setAnalyzeLoading(false);
        }
    };

    // ============ POSITION SIZER ============
    const handleCalculateSize = () => {
        const acct = parseFloat(sizeAccountValue);
        const risk = parseFloat(sizeRiskPercent);
        const entry = parseFloat(sizeEntryPrice);
        const stop = parseFloat(sizeStopLoss);

        if (!acct || !risk || !entry || !stop) {
            toast.error('Fill in all fields');
            return;
        }
        if (entry === stop) {
            toast.error('Entry and stop-loss cannot be the same');
            return;
        }

        const riskAmount = acct * (risk / 100);
        const riskPerShare = Math.abs(entry - stop);
        const shares = Math.floor(riskAmount / riskPerShare);
        const totalCost = shares * entry;
        const actualRisk = shares * riskPerShare;

        setSizeResult({
            shares,
            totalCost: totalCost.toFixed(2),
            riskAmount: riskAmount.toFixed(2),
            actualRisk: actualRisk.toFixed(2),
            riskPerShare: riskPerShare.toFixed(2),
            percentOfAccount: ((totalCost / acct) * 100).toFixed(2),
        });
    };

    // ============ ACTIVE POSITIONS ============
    const loadActivePositions = useCallback(async () => {
        if (!hasAlpacaKeys) return;
        setPositionsLoading(true);
        try {
            const res = await fetch('/api/positions/active', {
                headers: getAuthHeaders(),
            });
            const data = await res.json();
            if (data.success) {
                setActivePositions(data.data);
            }
        } catch (err) {
            console.error('Failed to load positions:', err);
        } finally {
            setPositionsLoading(false);
        }
    }, [hasAlpacaKeys]);

    useEffect(() => {
        if (activeTab === 'active') {
            loadActivePositions();
        }
    }, [activeTab, loadActivePositions]);

    // Auto-fill sizer from analyze result
    useEffect(() => {
        if (analyzeResult && analyzeResult.levels) {
            setSizeEntryPrice(analyzeResult.levels.entry.toString());
            setSizeStopLoss(analyzeResult.levels.stopLoss.toString());
        }
    }, [analyzeResult]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await loadActivePositions();
        setRefreshing(false);
    };

    // ============ HELPER COMPONENTS ============

    const StatCard = ({ label, value, subValue, icon: Icon, color = 'blue' }) => (
        <div className="bg-bg-tertiary rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
                {Icon && <Icon className={`w-4 h-4 text-accent-${color}`} />}
                <span className="text-xs text-text-muted uppercase tracking-wider">{label}</span>
            </div>
            <div className="text-xl font-bold">{value}</div>
            {subValue && <div className="text-xs text-text-muted mt-1">{subValue}</div>}
        </div>
    );

    const LevelRow = ({ label, price, color, distance }) => (
        <div className={`flex items-center justify-between py-2 px-3 rounded-lg bg-accent-${color}/10 border border-accent-${color}/20`}>
            <span className={`text-sm font-medium text-accent-${color}`}>{label}</span>
            <div className="text-right">
                <span className="text-sm font-mono font-bold">${price}</span>
                {distance && <span className="text-xs text-text-muted ml-2">({distance})</span>}
            </div>
        </div>
    );

    // ============ TAB CONTENT ============

    const tabs = [
        { id: 'calculator', label: 'ATR Calculator', icon: Calculator },
        { id: 'analyzer', label: 'Position Analyzer', icon: Crosshair },
        { id: 'sizer', label: 'Position Sizer', icon: DollarSign },
        { id: 'active', label: 'Active Positions', icon: Activity },
    ];

    return (
        <div className="min-h-screen bg-bg-primary text-text-primary pb-20 md:pb-6">
            <Navigation
                onRefresh={activeTab === 'active' ? handleRefresh : null}
                refreshing={refreshing}
            />

            <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6">
                {/* Page Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-display font-bold flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-accent-blue/15 flex items-center justify-center">
                            <Target className="w-5 h-5 text-accent-blue" />
                        </div>
                        Position Manager
                    </h1>
                    <p className="text-text-muted mt-1 ml-[52px]">ATR-based stops, targets, and position sizing</p>
                </div>

                {/* Tab Navigation */}
                <div className="flex gap-1 mb-6 overflow-x-auto pb-1">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                                activeTab === tab.id
                                    ? 'bg-accent-blue/15 text-accent-blue'
                                    : 'bg-bg-tertiary text-text-muted hover:text-text-primary hover:bg-bg-secondary'
                            }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* ============ ATR CALCULATOR TAB ============ */}
                {activeTab === 'calculator' && (
                    <div className="space-y-6">
                        <div className="card p-6">
                            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                <Calculator className="w-5 h-5 text-accent-blue" />
                                ATR Calculator
                            </h2>
                            <p className="text-sm text-text-muted mb-4">
                                Average True Range measures volatility. Higher ATR means wider stops needed.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <input
                                    type="text"
                                    value={atrSymbol}
                                    onChange={(e) => setAtrSymbol(e.target.value.toUpperCase())}
                                    onKeyDown={(e) => e.key === 'Enter' && handleATRLookup()}
                                    placeholder="Symbol (e.g. AAPL)"
                                    className="flex-1 px-4 py-3 bg-bg-tertiary border border-border-color rounded-xl text-text-primary placeholder-text-muted focus:border-accent-blue focus:outline-none"
                                />
                                <select
                                    value={atrPeriod}
                                    onChange={(e) => setAtrPeriod(parseInt(e.target.value))}
                                    className="px-4 py-3 bg-bg-tertiary border border-border-color rounded-xl text-text-primary focus:border-accent-blue focus:outline-none"
                                >
                                    <option value={7}>7-day ATR</option>
                                    <option value={14}>14-day ATR</option>
                                    <option value={21}>21-day ATR</option>
                                </select>
                                <button
                                    onClick={handleATRLookup}
                                    disabled={atrLoading}
                                    className="px-6 py-3 rounded-xl font-medium transition-all bg-accent-blue text-white hover:bg-accent-blue/80 disabled:opacity-50"
                                >
                                    {atrLoading ? (
                                        <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
                                    ) : (
                                        'Calculate'
                                    )}
                                </button>
                            </div>
                        </div>

                        {atrResult && (
                            <div className="card p-6">
                                <h3 className="text-lg font-semibold mb-4">
                                    {atrResult.symbol} — ATR Results
                                </h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                    <StatCard
                                        label="ATR Value"
                                        value={`$${atrResult.atr.atr}`}
                                        subValue={`${atrResult.atr.atrPercent}% of price`}
                                        icon={BarChart3}
                                        color="blue"
                                    />
                                    <StatCard
                                        label="Current Price"
                                        value={`$${atrResult.atr.currentPrice}`}
                                        icon={DollarSign}
                                        color="green"
                                    />
                                    <StatCard
                                        label="Day High"
                                        value={`$${atrResult.atr.high}`}
                                        icon={ArrowUpCircle}
                                        color="green"
                                    />
                                    <StatCard
                                        label="Day Low"
                                        value={`$${atrResult.atr.low}`}
                                        icon={ArrowDownCircle}
                                        color="red"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <h4 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">
                                        Suggested Levels (Long)
                                    </h4>
                                    <LevelRow
                                        label="Stop Loss (1.5x ATR)"
                                        price={atrResult.levels.stopLoss}
                                        color="red"
                                        distance={`-$${atrResult.levels.stopDistance}`}
                                    />
                                    <LevelRow
                                        label="Entry"
                                        price={atrResult.levels.entry}
                                        color="blue"
                                    />
                                    <LevelRow
                                        label="Target 1 (1x ATR)"
                                        price={atrResult.levels.target1}
                                        color="green"
                                        distance={`R:R ${atrResult.levels.riskReward1}`}
                                    />
                                    <LevelRow
                                        label="Target 2 (2x ATR)"
                                        price={atrResult.levels.target2}
                                        color="green"
                                        distance={`R:R ${atrResult.levels.riskReward2}`}
                                    />
                                    <LevelRow
                                        label="Target 3 (3x ATR)"
                                        price={atrResult.levels.target3}
                                        color="green"
                                        distance={`R:R ${atrResult.levels.riskReward3}`}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ============ POSITION ANALYZER TAB ============ */}
                {activeTab === 'analyzer' && (
                    <div className="space-y-6">
                        <div className="card p-6">
                            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                <Crosshair className="w-5 h-5 text-accent-purple" />
                                Position Analyzer
                            </h2>
                            <p className="text-sm text-text-muted mb-4">
                                Enter a symbol and optional entry price to get ATR-based stop and target levels.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                                <input
                                    type="text"
                                    value={analyzeSymbol}
                                    onChange={(e) => setAnalyzeSymbol(e.target.value.toUpperCase())}
                                    placeholder="Symbol (e.g. TSLA)"
                                    className="px-4 py-3 bg-bg-tertiary border border-border-color rounded-xl text-text-primary placeholder-text-muted focus:border-accent-blue focus:outline-none"
                                />
                                <input
                                    type="number"
                                    value={analyzeEntry}
                                    onChange={(e) => setAnalyzeEntry(e.target.value)}
                                    placeholder="Entry price (optional, uses current)"
                                    step="0.01"
                                    className="px-4 py-3 bg-bg-tertiary border border-border-color rounded-xl text-text-primary placeholder-text-muted focus:border-accent-blue focus:outline-none"
                                />
                            </div>
                            <div className="flex gap-3 mb-4">
                                <button
                                    onClick={() => setAnalyzeSide('long')}
                                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all ${
                                        analyzeSide === 'long'
                                            ? 'bg-accent-green/15 text-accent-green border border-accent-green/30'
                                            : 'bg-bg-tertiary text-text-muted border border-border-color'
                                    }`}
                                >
                                    <ArrowUpCircle className="w-4 h-4" /> Long
                                </button>
                                <button
                                    onClick={() => setAnalyzeSide('short')}
                                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all ${
                                        analyzeSide === 'short'
                                            ? 'bg-accent-red/15 text-accent-red border border-accent-red/30'
                                            : 'bg-bg-tertiary text-text-muted border border-border-color'
                                    }`}
                                >
                                    <ArrowDownCircle className="w-4 h-4" /> Short
                                </button>
                            </div>
                            <button
                                onClick={handleAnalyze}
                                disabled={analyzeLoading}
                                className="w-full px-6 py-3 rounded-xl font-medium transition-all bg-accent-purple text-white hover:bg-accent-purple/80 disabled:opacity-50"
                            >
                                {analyzeLoading ? (
                                    <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
                                ) : (
                                    'Analyze Position'
                                )}
                            </button>
                        </div>

                        {analyzeResult && (
                            <div className="card p-6">
                                <h3 className="text-lg font-semibold mb-2">
                                    {analyzeResult.symbol} — {analyzeResult.levels.side.toUpperCase()} Analysis
                                </h3>
                                <p className="text-sm text-text-muted mb-4">
                                    ATR({analyzeResult.atr.period}): ${analyzeResult.atr.atr} ({analyzeResult.atr.atrPercent}% of price)
                                </p>

                                <div className="space-y-2 mb-6">
                                    {analyzeResult.levels.side === 'long' ? (
                                        <>
                                            <LevelRow label="Stop Loss" price={analyzeResult.levels.stopLoss} color="red" distance={`-$${analyzeResult.levels.stopDistance}`} />
                                            <LevelRow label="Entry" price={analyzeResult.levels.entry} color="blue" />
                                            <LevelRow label="Breakeven" price={analyzeResult.levels.breakeven} color="yellow" distance="Move here after T1" />
                                            <LevelRow label="Target 1" price={analyzeResult.levels.target1} color="green" distance={`R:R ${analyzeResult.levels.riskReward1}`} />
                                            <LevelRow label="Target 2" price={analyzeResult.levels.target2} color="green" distance={`R:R ${analyzeResult.levels.riskReward2}`} />
                                            <LevelRow label="Target 3" price={analyzeResult.levels.target3} color="green" distance={`R:R ${analyzeResult.levels.riskReward3}`} />
                                        </>
                                    ) : (
                                        <>
                                            <LevelRow label="Target 3" price={analyzeResult.levels.target3} color="green" distance={`R:R ${analyzeResult.levels.riskReward3}`} />
                                            <LevelRow label="Target 2" price={analyzeResult.levels.target2} color="green" distance={`R:R ${analyzeResult.levels.riskReward2}`} />
                                            <LevelRow label="Target 1" price={analyzeResult.levels.target1} color="green" distance={`R:R ${analyzeResult.levels.riskReward1}`} />
                                            <LevelRow label="Breakeven" price={analyzeResult.levels.breakeven} color="yellow" distance="Move here after T1" />
                                            <LevelRow label="Entry" price={analyzeResult.levels.entry} color="blue" />
                                            <LevelRow label="Stop Loss" price={analyzeResult.levels.stopLoss} color="red" distance={`+$${analyzeResult.levels.stopDistance}`} />
                                        </>
                                    )}
                                </div>

                                <div className="bg-bg-tertiary rounded-xl p-4">
                                    <h4 className="text-sm font-semibold text-text-muted mb-2 flex items-center gap-2">
                                        <Info className="w-4 h-4" /> Exit Strategy
                                    </h4>
                                    <ul className="text-sm text-text-secondary space-y-1">
                                        <li>1. Sell 33% at Target 1, move stop to breakeven</li>
                                        <li>2. Sell 33% at Target 2, trail stop to Target 1</li>
                                        <li>3. Close remaining at Target 3</li>
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ============ POSITION SIZER TAB ============ */}
                {activeTab === 'sizer' && (
                    <div className="space-y-6">
                        <div className="card p-6">
                            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                <DollarSign className="w-5 h-5 text-accent-green" />
                                Position Sizer
                            </h2>
                            <p className="text-sm text-text-muted mb-4">
                                Calculate the right number of shares based on your risk tolerance.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                                <div>
                                    <label className="block text-xs text-text-muted mb-1 uppercase tracking-wider">Account Value ($)</label>
                                    <input
                                        type="number"
                                        value={sizeAccountValue}
                                        onChange={(e) => setSizeAccountValue(e.target.value)}
                                        placeholder="e.g. 80000"
                                        className="w-full px-4 py-3 bg-bg-tertiary border border-border-color rounded-xl text-text-primary placeholder-text-muted focus:border-accent-blue focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-text-muted mb-1 uppercase tracking-wider">Risk Per Trade (%)</label>
                                    <input
                                        type="number"
                                        value={sizeRiskPercent}
                                        onChange={(e) => setSizeRiskPercent(e.target.value)}
                                        placeholder="e.g. 1"
                                        step="0.1"
                                        className="w-full px-4 py-3 bg-bg-tertiary border border-border-color rounded-xl text-text-primary placeholder-text-muted focus:border-accent-blue focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-text-muted mb-1 uppercase tracking-wider">Entry Price ($)</label>
                                    <input
                                        type="number"
                                        value={sizeEntryPrice}
                                        onChange={(e) => setSizeEntryPrice(e.target.value)}
                                        placeholder="e.g. 185.50"
                                        step="0.01"
                                        className="w-full px-4 py-3 bg-bg-tertiary border border-border-color rounded-xl text-text-primary placeholder-text-muted focus:border-accent-blue focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-text-muted mb-1 uppercase tracking-wider">Stop Loss ($)</label>
                                    <input
                                        type="number"
                                        value={sizeStopLoss}
                                        onChange={(e) => setSizeStopLoss(e.target.value)}
                                        placeholder="e.g. 180.00"
                                        step="0.01"
                                        className="w-full px-4 py-3 bg-bg-tertiary border border-border-color rounded-xl text-text-primary placeholder-text-muted focus:border-accent-blue focus:outline-none"
                                    />
                                </div>
                            </div>
                            <button
                                onClick={handleCalculateSize}
                                className="w-full px-6 py-3 rounded-xl font-medium transition-all bg-accent-green text-white hover:bg-accent-green/80"
                            >
                                Calculate Position Size
                            </button>
                        </div>

                        {sizeResult && (
                            <div className="card p-6">
                                <h3 className="text-lg font-semibold mb-4">Position Size Result</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <StatCard
                                        label="Shares to Buy"
                                        value={sizeResult.shares}
                                        icon={Layers}
                                        color="blue"
                                    />
                                    <StatCard
                                        label="Total Cost"
                                        value={`$${sizeResult.totalCost}`}
                                        subValue={`${sizeResult.percentOfAccount}% of account`}
                                        icon={DollarSign}
                                        color="green"
                                    />
                                    <StatCard
                                        label="Max Risk"
                                        value={`$${sizeResult.actualRisk}`}
                                        subValue={`$${sizeResult.riskPerShare}/share`}
                                        icon={Shield}
                                        color="red"
                                    />
                                </div>
                                {parseFloat(sizeResult.percentOfAccount) > 25 && (
                                    <div className="mt-4 p-3 rounded-xl bg-accent-yellow/10 border border-accent-yellow/20 flex items-start gap-2">
                                        <AlertTriangle className="w-4 h-4 text-accent-yellow mt-0.5 flex-shrink-0" />
                                        <span className="text-sm text-accent-yellow">
                                            This position uses {sizeResult.percentOfAccount}% of your account. Consider reducing size for better diversification.
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* ============ ACTIVE POSITIONS TAB ============ */}
                {activeTab === 'active' && (
                    <div className="space-y-4">
                        {!hasAlpacaKeys ? (
                            <div className="card p-8 text-center">
                                <AlertTriangle className="w-12 h-12 text-accent-yellow mx-auto mb-3" />
                                <h3 className="text-lg font-semibold mb-2">Alpaca Keys Required</h3>
                                <p className="text-text-muted">Go to Settings and enter your Alpaca API keys to view active positions.</p>
                            </div>
                        ) : positionsLoading ? (
                            <div className="card p-8 text-center">
                                <RefreshCw className="w-8 h-8 animate-spin text-accent-blue mx-auto mb-3" />
                                <p className="text-text-muted">Loading positions with ATR data...</p>
                            </div>
                        ) : activePositions.length === 0 ? (
                            <div className="card p-8 text-center">
                                <CheckCircle className="w-12 h-12 text-accent-green mx-auto mb-3" />
                                <h3 className="text-lg font-semibold mb-2">No Active Positions</h3>
                                <p className="text-text-muted">Your Alpaca account has no open positions. Use the ATR Calculator to plan your next trade.</p>
                            </div>
                        ) : (
                            activePositions.map((pos) => (
                                <div key={pos.symbol} className="card overflow-hidden">
                                    <button
                                        onClick={() => setExpandedPosition(expandedPosition === pos.symbol ? null : pos.symbol)}
                                        className="w-full p-4 flex items-center justify-between hover:bg-bg-secondary/50 transition-all"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                                pos.unrealizedPl >= 0 ? 'bg-accent-green/15' : 'bg-accent-red/15'
                                            }`}>
                                                {pos.unrealizedPl >= 0 ? (
                                                    <TrendingUp className="w-5 h-5 text-accent-green" />
                                                ) : (
                                                    <TrendingDown className="w-5 h-5 text-accent-red" />
                                                )}
                                            </div>
                                            <div className="text-left">
                                                <div className="font-semibold">{pos.symbol}</div>
                                                <div className="text-xs text-text-muted">
                                                    {pos.qty} shares @ ${pos.entryPrice.toFixed(2)} • {pos.side.toUpperCase()}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="text-right">
                                                <div className={`font-semibold ${pos.unrealizedPl >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                                                    {pos.unrealizedPl >= 0 ? '+' : ''}${pos.unrealizedPl.toFixed(2)}
                                                </div>
                                                <div className={`text-xs ${pos.unrealizedPlPercent >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                                                    {pos.unrealizedPlPercent >= 0 ? '+' : ''}{pos.unrealizedPlPercent.toFixed(2)}%
                                                </div>
                                            </div>
                                            {expandedPosition === pos.symbol ? (
                                                <ChevronUp className="w-5 h-5 text-text-muted" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-text-muted" />
                                            )}
                                        </div>
                                    </button>

                                    {expandedPosition === pos.symbol && (
                                        <div className="px-4 pb-4 border-t border-border-color pt-4">
                                            {pos.atr && pos.levels ? (
                                                <div className="space-y-3">
                                                    <div className="grid grid-cols-3 gap-3">
                                                        <StatCard label="ATR" value={`$${pos.atr.atr}`} subValue={`${pos.atr.atrPercent}%`} icon={BarChart3} color="blue" />
                                                        <StatCard label="Current" value={`$${pos.currentPrice.toFixed(2)}`} icon={DollarSign} color="green" />
                                                        <StatCard label="Mkt Value" value={`$${pos.marketValue.toFixed(2)}`} icon={Layers} color="purple" />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <LevelRow label="Stop Loss" price={pos.levels.stopLoss} color="red" distance={`-$${pos.levels.stopDistance}`} />
                                                        <LevelRow label="Entry" price={pos.levels.entry} color="blue" />
                                                        <LevelRow label="Target 1" price={pos.levels.target1} color="green" distance={`R:R ${pos.levels.riskReward1}`} />
                                                        <LevelRow label="Target 2" price={pos.levels.target2} color="green" distance={`R:R ${pos.levels.riskReward2}`} />
                                                        <LevelRow label="Target 3" price={pos.levels.target3} color="green" distance={`R:R ${pos.levels.riskReward3}`} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-center py-4 text-text-muted text-sm">
                                                    <AlertTriangle className="w-5 h-5 mx-auto mb-2 text-accent-yellow" />
                                                    ATR data unavailable: {pos.atrError || 'Unknown error'}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PositionManager;
